import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { CloseIcon } from '@blade/ui-kit'
import { type PairModel } from '@root/entities/pair'
import { Provider } from '@root/entities/provider'
import { useOptionalAtom } from '@root/shared/lib'
import { ExclamationIcon } from '@root/shared/ui-kit'

type TradingLiquidityAlertProps = {
  pair: PairModel | undefined
}

const PAIR_LOW_LIQUIDITY_THRESHOLD = 500

export const TradingLiquidityAlert: React.FC<TradingLiquidityAlertProps> = ({
  pair,
}) => {
  const { t } = useTranslation()
  const liquidityUsd = useOptionalAtom(pair?.liquidityUsd)

  const isLowLiquidity = useMemo(() => {
    if (pair?.provider === Provider.pumpFun) {
      return false
    }
    return liquidityUsd?.lt(PAIR_LOW_LIQUIDITY_THRESHOLD)
  }, [liquidityUsd, pair?.provider])

  const [forceClosed, setForceClosed] = useState(false)

  const isOpen = !forceClosed && isLowLiquidity

  const handleClose = () => setForceClosed(true)

  return (
    <AnimatePresence>
      {isOpen ? (
        <Root
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <AttentionIcon />
          <span>{t('trading.low-liquidity-alert')}</span>
          <Close size={22} onClick={handleClose} />
        </Root>
      ) : undefined}
    </AnimatePresence>
  )
}

const Root = styled(motion.div)`
  background: var(--warningA-3);
  color: var(--warning-9);

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 2px;
  border-radius: 8px;
  padding: 5px;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const AttentionIcon = styled(ExclamationIcon)`
  transform: rotate(180deg);
`

const Close = styled(CloseIcon)`
  color: var(--textA-5);
  cursor: pointer;

  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--textA-9);
  }
`
